import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '@/lib/api/arrex/settings';
import { readItems, field } from '@/lib/api/normalize';
import { ToggleField } from '@/components/ui/toggle-field';
import { toast } from 'sonner';
import { Sparkles, Trash2, ArrowUp, ArrowDown, Loader2, ImageIcon, Upload } from 'lucide-react';
import { ImageUploader } from '@/components/admin/ImageUploader';
import { FormSkeleton } from '@/components/ui/skeletons';
import { imgUrl } from '@/lib/format';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';


// ─── Slide card (shared between mobile scroll and desktop grid) ───────────────
function SlideCard({ s, i, slides, t, field, imgUrl, removeSlide, moveSlide, toggleSlide, updateSlideUrl }: {
  s: any; i: number; slides: any[];
  t: any; field: any; imgUrl: any;
  removeSlide: (id: string) => void;
  moveSlide: (i: number, dir: -1 | 1) => void;
  toggleSlide: (id: string, v: boolean) => void;
  updateSlideUrl: (id: string, url: string | null) => void;
}) {
  const image: string = field(s, 'image_url') ?? '';
  const visible: boolean = field(s, 'is_visible') ?? true;
  return (
    <div className="rounded-xl overflow-hidden border border-border bg-card snap-start">
      {/* Square image area */}
      <div className="aspect-square bg-muted relative">
        <img src={imgUrl(image)} alt="" className="w-full h-full object-cover" />

        {/* Top-right: delete */}
        <button
          onClick={() => removeSlide(s.id)}
          className="absolute top-1.5 end-1.5 h-6 w-6 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-destructive flex items-center justify-center transition-colors"
          aria-label="Delete"
        >
          <Trash2 className="w-3 h-3" />
        </button>

        {/* Top-left: reorder */}
        <div className="absolute top-1.5 start-1.5 flex flex-col gap-1">
          <button
            onClick={() => moveSlide(i, -1)}
            disabled={i === 0}
            className="h-6 w-6 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-primary disabled:opacity-30 flex items-center justify-center transition-colors"
            aria-label="Move up"
          >
            <ArrowUp className="w-3 h-3" />
          </button>
          <button
            onClick={() => moveSlide(i, 1)}
            disabled={i === slides.length - 1}
            className="h-6 w-6 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-primary disabled:opacity-30 flex items-center justify-center transition-colors"
            aria-label="Move down"
          >
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>

        {/* Bottom-left: index badge */}
        <div className="absolute bottom-1.5 start-1.5 text-[9px] font-mono uppercase bg-black/60 backdrop-blur-sm text-white px-1.5 py-0.5 rounded">
          #{i + 1}
        </div>
      </div>

      {/* Bottom bar: toggle + replace */}
      <div className="p-2 space-y-2 bg-background/95 border-t border-border">
        <ToggleField
          checked={visible}
          onCheckedChange={(v) => toggleSlide(s.id, v)}
          size="sm"
          spread
        />
        <ImageUploader
          value={image}
          onChange={(url) => updateSlideUrl(s.id, url)}
          bucket="site-assets"
          pathPrefix="hero-slides/"
          aspect="hero"
          outputWidth={1920}
          inline
          buttonLabel={t('common.replace')}
          className="[&>div]:w-full"
          buttonClassName="w-full h-8 justify-center rounded-full border-primary/40 bg-primary/10 text-[11px] font-medium uppercase tracking-widest text-foreground hover:bg-primary hover:text-primary-foreground transition-colors sm:h-9 sm:text-xs"
        />
      </div>
    </div>
  );
}

export default function AdminAppearance() {
  const { t } = useTranslation();
  const [slides, setSlides] = useState<any[] | null>(null);
  const [ethosImage, setEthosImage] = useState<string>('');
  const [ethosLoading, setEthosLoading] = useState(true);
  const [ethosSaving, setEthosSaving] = useState(false);

  useEffect(() => { void loadSlides(); void loadEthos(); }, []);

  const loadEthos = async () => {
    try {
      const { settings } = await settingsApi.adminGet();
      setEthosImage(settings?.ethos_image || '');
    } catch {
      setEthosImage('');
    } finally {
      setEthosLoading(false);
    }
  };

  const saveEthos = async (url: string | null) => {
    setEthosSaving(true);
    try {
      const { settings } = await settingsApi.adminGet();
      await settingsApi.update({ ...settings, ethos_image: url || '' });
      setEthosImage(url || '');
      toast.success(t('admin.appearancePage.ethosSaved', { defaultValue: 'Ethos image updated' }));
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setEthosSaving(false);
    }
  };

  const loadSlides = async () => {
    try {
      setSlides(readItems(await settingsApi.adminHeroSlides()));
    } catch {
      setSlides([]);
    }
  };

  const addSlideUrl = async (url: string | null) => {
    if (!url) return;
    try {
      await settingsApi.createHeroSlide(url);
      toast.success('Slide added');
      loadSlides();
    } catch (e: any) { toast.error(e?.message || 'Failed to add'); }
  };

  const updateSlideUrl = async (id: string, url: string | null) => {
    if (!url) return;
    try {
      await settingsApi.updateHeroSlide(id, { image_url: url });
      loadSlides();
    } catch (e: any) { toast.error(e?.message || 'Failed to update'); }
  };

  const removeSlide = async (id: string) => {
    try {
      await settingsApi.removeHeroSlide(id);
      loadSlides();
    } catch (e: any) { toast.error(e?.message || 'Failed to delete'); }
  };

  const toggleSlide = async (id: string, is_visible: boolean) => {
    try {
      await settingsApi.updateHeroSlide(id, { is_visible });
      loadSlides();
    } catch (e: any) { toast.error(e?.message || 'Failed to update'); }
  };

  const moveSlide = async (idx: number, dir: -1 | 1) => {
    if (!slides) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= slides.length) return;
    const a = slides[idx], b = slides[swap];
    try {
      await settingsApi.updateHeroSlide(a.id, { sort_order: field<number>(b, 'sort_order') ?? swap });
      await settingsApi.updateHeroSlide(b.id, { sort_order: field<number>(a, 'sort_order') ?? idx });
      loadSlides();
    } catch (e: any) { toast.error(e?.message || 'Failed to reorder'); }
  };

  if (!slides) return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Sparkles className="w-7 h-7 text-primary" />{t('admin.appearancePage.title')}</h1>
      <FormSkeleton rows={6} />
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Sparkles className="w-7 h-7 text-primary" />{t('admin.appearancePage.title')}</h1>

      <section className="surface-card rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-display text-lg">{t('admin.appearancePage.heroTitle')}</h2>
          <ImageUploader
            value={null}
            onChange={addSlideUrl}
            bucket="site-assets"
            pathPrefix="hero-slides/"
            aspect="hero"
            responsivePreview
            outputWidth={1920}
          />
        </div>
        <p className="text-xs text-muted-foreground">{t('admin.appearancePage.heroNote')}</p>
        {slides.length === 0 && <div className="text-sm text-muted-foreground">{t('admin.appearancePage.heroEmpty')}</div>}

        {/* Swiper carousel — 2.5 cards visible on mobile, scaling up per breakpoint */}
        <Swiper
          modules={[FreeMode]}
          freeMode={{ enabled: true, sticky: true }}
          spaceBetween={12}
          slidesPerView={2.5}
          breakpoints={{
            640: { slidesPerView: 3.5 },
            768: { slidesPerView: 3.5, spaceBetween: 14 },
            1024: { slidesPerView: 4.5, spaceBetween: 14 },
          }}
          dir="ltr"
          className="-mx-6 px-6 md:mx-0 md:px-0"
        >
          {slides.map((s, i) => (
            <SwiperSlide key={s.id}>
              <SlideCard s={s} i={i} slides={slides} t={t} field={field} imgUrl={imgUrl} removeSlide={removeSlide} moveSlide={moveSlide} toggleSlide={toggleSlide} updateSlideUrl={updateSlideUrl} />
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Brand Ethos Image                                                   */}
      {/* ------------------------------------------------------------------ */}
      <section className="surface-card rounded-lg p-6 space-y-4">
        <div>
          <h2 className="font-display text-lg flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-primary shrink-0" />
            {t('admin.appearancePage.ethosTitle', { defaultValue: 'Brand Ethos Image' })}
          </h2>
          <p className="text-xs text-muted-foreground mt-1 ms-7">
            {t('admin.appearancePage.ethosNote', { defaultValue: '"Our Philosophy" section image on the homepage. Recommended portrait/square ratio.' })}
          </p>
        </div>

        {ethosLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('admin.common.loading', { defaultValue: 'Loading...' })}
          </div>
        ) : (
          <div className="w-44">
            {ethosImage ? (
              <div className="rounded-xl overflow-hidden border border-border bg-card">
                <div className="aspect-square bg-muted relative">
                  <img src={imgUrl(ethosImage)} alt="Ethos" className="w-full h-full object-cover" />
                  
                  {/* Top-right: delete */}
                  <button
                    type="button"
                    onClick={() => saveEthos(null)}
                    disabled={ethosSaving}
                    className="absolute top-1.5 end-1.5 h-6 w-6 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-destructive flex items-center justify-center transition-colors disabled:opacity-50"
                    aria-label={t('admin.appearancePage.ethosRemove')}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  {ethosSaving && (
                    <div className="absolute inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  )}
                </div>
                
                {/* Bottom bar: Replace button */}
                <div className="px-2 py-2 flex items-center justify-center bg-background/95 border-t border-border">
                  <ImageUploader
                    value={ethosImage}
                    onChange={saveEthos}
                    bucket="site-assets"
                    pathPrefix="ethos/"
                    aspect="square"
                    outputWidth={900}
                    inline
                    buttonLabel={t('common.replace')}
                    className="[&>div]:w-full"
                  />
                </div>
              </div>
            ) : (
              <ImageUploader
                value={null}
                onChange={saveEthos}
                bucket="site-assets"
                pathPrefix="ethos/"
                aspect="square"
                outputWidth={900}
              >
                {(onClick, busy) => (
                  <button
                    type="button"
                    onClick={onClick}
                    disabled={busy || ethosSaving}
                    className="w-full aspect-square rounded-xl border border-dashed border-border hover:border-primary/50 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary bg-card/50"
                  >
                    {busy || ethosSaving ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        <span className="text-xs font-medium">{t('admin.appearancePage.ethosUpload')}</span>
                      </>
                    )}
                  </button>
                )}
              </ImageUploader>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
